import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit2, Trash2, Building2, CheckSquare, XSquare, RefreshCw, Bell, QrCode, Settings, UserMinus, X, Copy, CheckCircle, ShieldCheck, Clock, Headphones, Image as ImageIcon, MessageSquare, Search, Power, Sparkles, Mail, MapPin } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import EventForm from '../components/shared/EventForm';
import ToggleSwitch from '../components/shared/ToggleSwitch';
import JudgeInvitationManager from '../components/admin/JudgeInvitationManager';
import { eventAccessService, eventService, organizerService, clubService } from '../services/supabaseService';
import { eventClubServiceProxy } from '../services/proxyService';
import { useConfirm } from '../components/shared/ConfirmContext';
import { useNotifications } from '../components/shared/useNotifications';
import '../components/admin/AdminView.css';
import logger from '../utils/logger';

const AdminView = ({
  events,
  showEventForm,
  setShowEventForm,
  editingEvent,
  setEditingEvent,
  eventForm,
  setEventForm,
  addEvent,
  deleteEvent,
  editEvent,
  uploadingImage,
  handleFileChange,
  clearImagePreview,
  cancelEventForm,
  clubs = [],
  judges = [],
  updateEvent = null, // Nueva prop para actualizar estado del evento
  auth = null // Admin auth info
}) => {
  // Inicializar con mes y año actual
  const today = new Date();
  const confirm = useConfirm();
  const notify = useNotifications();
  const currentYear = today.getFullYear();
  const currentMonth = String(today.getMonth() + 1).padStart(2, '0');
  
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [adminTab, setAdminTab] = useState('events');

  // Solicitudes state
  const [accessRequests, setAccessRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [solicitudesEventFilter, setSolicitudesEventFilter] = useState('all');

  // QR modal
  const [qrModal, setQrModal] = useState(null);
  const [codeLoading, setCodeLoading] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  // Manage event modal
  const [managingEvent, setManagingEvent] = useState(null);
  const [managingRequests, setManagingRequests] = useState([]);
  const [managingLoading, setManagingLoading] = useState(false);

  // Pending event approvals
  const [pendingEvents, setPendingEvents] = useState([]);
  const [pendingEventsLoading, setPendingEventsLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState({});

  // Pending organizer approvals
  const [pendingOrganizers, setPendingOrganizers] = useState([]);
  const [pendingOrganizersLoading, setPendingOrganizersLoading] = useState(false);
  const [organizerApprovalNotes, setOrganizerApprovalNotes] = useState({});

  // Clubes (gestion completa - admin)
  const [allClubs, setAllClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);
  const [clubsFilter, setClubsFilter] = useState('pending_approval'); // pending_approval | approved | rejected | all
  const [clubsSearch, setClubsSearch] = useState('');
  const [clubAdminNotes, setClubAdminNotes] = useState({}); // { [clubId]: 'notes' }
  const [expandedClubId, setExpandedClubId] = useState(null);

  const loadAccessRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const { data } = await eventAccessService.getPendingRequests();
      setAccessRequests(data || []);
    } catch (err) {
      logger.error('Error loading access requests:', err?.message);
    } finally {
      setRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccessRequests();
  }, [loadAccessRequests]);

  // Load pending events for approval
  const loadPendingEvents = useCallback(async () => {
    setPendingEventsLoading(true);
    try {
      const { data } = await eventService.getPendingApproval();
      setPendingEvents(data || []);
    } catch (err) {
      logger.error('Error loading pending events:', err?.message);
    } finally {
      setPendingEventsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingEvents();
  }, [loadPendingEvents]);

  const loadPendingOrganizers = useCallback(async () => {
    setPendingOrganizersLoading(true);
    try {
      const { data } = await organizerService.getPendingRegistrations();
      setPendingOrganizers(data || []);
    } catch (err) {
      logger.error('Error loading pending organizers:', err?.message);
    } finally {
      setPendingOrganizersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPendingOrganizers();
  }, [loadPendingOrganizers]);

  // ==============================
  // CLUBES — Gestion completa admin
  // ==============================
  const loadAllClubs = useCallback(async () => {
    setClubsLoading(true);
    try {
      const { data } = await clubService.getAll();
      setAllClubs(data || []);
    } catch (err) {
      logger.error('Error loading clubs:', err?.message);
    } finally {
      setClubsLoading(false);
    }
  }, []);

  useEffect(() => { loadAllClubs(); }, [loadAllClubs]);

  const handleApproveClub = async (club) => {
    const notes = clubAdminNotes[club.id] || '';
    const { error } = await clubService.approveRegistration(club.id, notes);
    if (error) { notify.error('Error al aprobar: ' + error); return; }
    notify.success(`Club "${club.name}" aprobado`);
    setClubAdminNotes(prev => { const n = { ...prev }; delete n[club.id]; return n; });
    loadAllClubs();
  };

  const handleRejectClub = async (club) => {
    const notes = clubAdminNotes[club.id] || '';
    if (!notes.trim()) {
      notify.warning('Agrega una nota explicando el motivo del rechazo antes de continuar.');
      return;
    }
    const ok = await confirm({
      title: 'Rechazar club',
      message: `¿Rechazar el registro de "${club.name}"? El club no podrá iniciar sesión.`,
      confirmText: 'Rechazar',
      type: 'danger',
    });
    if (!ok) return;
    const { error } = await clubService.rejectRegistration(club.id, notes);
    if (error) { notify.error('Error al rechazar: ' + error); return; }
    notify.success(`Club "${club.name}" rechazado`);
    setClubAdminNotes(prev => { const n = { ...prev }; delete n[club.id]; return n; });
    loadAllClubs();
  };

  const handleToggleClubActive = async (club) => {
    const newActive = !club.is_active;
    const action = newActive ? 'Reactivar' : 'Suspender';
    const ok = await confirm({
      title: `${action} club`,
      message: newActive
        ? `¿Reactivar a "${club.name}"? Podrá iniciar sesión nuevamente.`
        : `¿Suspender a "${club.name}"? No podrá iniciar sesión hasta que lo reactives.`,
      confirmText: action,
      type: newActive ? 'info' : 'danger',
    });
    if (!ok) return;
    const { error } = await clubService.update(club.id, { is_active: newActive });
    if (error) { notify.error('Error: ' + error); return; }
    notify.success(`Club ${newActive ? 'reactivado' : 'suspendido'}`);
    loadAllClubs();
  };

  const handleTogglePoseAnalysis = async (club) => {
    const newVal = !club.pose_analysis_enabled;
    const { error } = await clubService.update(club.id, { pose_analysis_enabled: newVal });
    if (error) { notify.error('Error: ' + error); return; }
    notify.success(`Análisis IA ${newVal ? 'activado' : 'desactivado'} para "${club.name}"`);
    loadAllClubs();
  };

  const handleDeleteClub = async (club) => {
    const ok = await confirm({
      title: 'Eliminar club permanentemente',
      message: `¿ELIMINAR "${club.name}"? Esto borra también sus gimnastas e imágenes asociadas. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      type: 'danger',
    });
    if (!ok) return;
    const { error } = await clubService.delete(club.id);
    if (error) { notify.error('Error al eliminar: ' + error); return; }
    notify.success(`Club "${club.name}" eliminado`);
    loadAllClubs();
  };

  const pendingClubsCount = useMemo(
    () => allClubs.filter(c => c.approval_status === 'pending_approval').length,
    [allClubs]
  );

  const filteredClubs = useMemo(() => {
    let list = allClubs;
    if (clubsFilter !== 'all') {
      list = list.filter(c => (c.approval_status || 'approved') === clubsFilter);
    }
    const q = clubsSearch.trim().toLowerCase();
    if (q) {
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.location || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [allClubs, clubsFilter, clubsSearch]);

  const handleApproveEvent = async (eventId) => {
    try {
      const notes = approvalNotes[eventId] || '';
      await eventService.approveEvent(eventId, notes);
      setPendingEvents(prev => prev.filter(e => e.id !== eventId));
      setApprovalNotes(prev => { const n = { ...prev }; delete n[eventId]; return n; });
    } catch (err) { logger.error('Error approving event:', err?.message); }
  };

  const handleRejectEvent = async (eventId) => {
    const notes = approvalNotes[eventId] || '';
    if (!notes.trim()) {
      notify.warning('Por favor, agrega una nota explicando el motivo del rechazo.');
      return;
    }
    try {
      await eventService.rejectEvent(eventId, notes);
      setPendingEvents(prev => prev.filter(e => e.id !== eventId));
      setApprovalNotes(prev => { const n = { ...prev }; delete n[eventId]; return n; });
    } catch (err) { logger.error('Error rejecting event:', err?.message); }
  };

  const handleApproveOrganizer = async (organizer) => {
    try {
      const notes = organizerApprovalNotes[organizer.id] || '';
      await organizerService.approveRegistration(organizer.id, notes);
      setPendingOrganizers(prev => prev.filter(o => o.id !== organizer.id));
      setOrganizerApprovalNotes(prev => { const n = { ...prev }; delete n[organizer.id]; return n; });
      notify.success(`Organizador "${organizer.name}" aprobado correctamente`);
    } catch (err) {
      console.error('Error approving organizer:', err?.message);
      notify.error('Error al aprobar el organizador');
    }
  };

  const handleRejectOrganizer = async (organizer) => {
    const notes = organizerApprovalNotes[organizer.id] || '';
    if (!notes.trim()) {
      notify.warning('Por favor, agrega una nota explicando el motivo del rechazo.');
      return;
    }
    try {
      await organizerService.rejectRegistration(organizer.id, notes);
      setPendingOrganizers(prev => prev.filter(o => o.id !== organizer.id));
      setOrganizerApprovalNotes(prev => { const n = { ...prev }; delete n[organizer.id]; return n; });
      notify.success(`Organizador "${organizer.name}" rechazado`);
    } catch (err) {
      console.error('Error rejecting organizer:', err?.message);
      notify.error('Error al rechazar el organizador');
    }
  };

  const handleApproveRequest = async (id) => {
    try {
      await eventAccessService.approveRequest(id);
      setAccessRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) { logger.error('Error:', err?.message); }
  };

  const handleRejectRequest = async (id) => {
    try {
      await eventAccessService.rejectRequest(id);
      setAccessRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) { logger.error('Error:', err?.message); }
  };

  // QR
  const handleShowQR = async (event) => {
    setCodeLoading(event.id);
    try {
      let { data: codeData } = await eventAccessService.getEventCode(event.id);
      if (!codeData) {
        const genResult = await eventAccessService.generateEventCode(event.id);
        codeData = genResult.data;
      }
      if (codeData) setQrModal({ eventId: event.id, eventName: event.name, code: codeData.code });
    } catch (err) { logger.error('Error loading code:', err?.message); }
    finally { setCodeLoading(null); }
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Manage event
  const handleManageEvent = async (event) => {
    setManagingEvent(event);
    setManagingLoading(true);
    try {
      const { data } = await eventAccessService.getRequestsByEvents([event.id]);
      setManagingRequests(data || []);
    } catch (err) { logger.error('Error:', err?.message); }
    finally { setManagingLoading(false); }
  };

  const handleManageApprove = async (id) => {
    try {
      await eventAccessService.approveRequest(id);
      setManagingRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'approved' } : r));
      setAccessRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) { logger.error('Error:', err?.message); }
  };

  const handleManageReject = async (id) => {
    try {
      await eventAccessService.rejectRequest(id);
      setManagingRequests(prev => prev.map(r => r.id === id ? { ...r, status: 'rejected' } : r));
      setAccessRequests(prev => prev.filter(r => r.id !== id));
    } catch (err) { logger.error('Error:', err?.message); }
  };

  const handleRemoveClubFromEvent = async (requestId) => {
    const ok = await confirm({
      title: 'Eliminar Club del Evento',
      message: '¿Estás seguro de eliminar este club del evento?',
      type: 'danger',
      confirmText: 'Eliminar'
    });
    if (!ok) return;
    try {
      const { error } = await eventClubServiceProxy.deleteById(requestId);
      if (!error) {
        setManagingRequests(prev => prev.filter(r => r.id !== requestId));
      }
    } catch (err) { logger.error('Error:', err?.message); }
  };

  // Filtered solicitudes
  const filteredSolicitudes = useMemo(() => {
    if (solicitudesEventFilter === 'all') return accessRequests;
    return accessRequests.filter(r => r.events?.id === solicitudesEventFilter);
  }, [accessRequests, solicitudesEventFilter]);

  // Generar rangos de años (desde 2024 hasta 2030)
  const yearOptions = Array.from({ length: 7 }, (_, i) => 2024 + i);

  // Generar meses
  const monthNames = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' }
  ];

  // Filtrar eventos del mes/año seleccionado
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.date) return false;
      const [eventYear, eventMonth] = event.date.split('-');
      return eventYear === selectedYear && eventMonth === selectedMonth;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [events, selectedYear, selectedMonth]);

  const handleToggleEventStatus = async (event) => {
    if (!updateEvent) {
      return;
    }
    
    try {
      await updateEvent({
        ...event,
        is_active: !event.is_active
      });
    } catch (error) {
      logger.error('Error updating event status:', error?.message);
    }
  };

  if (showEventForm) {
    return (
      <div className="admin-view">
        <EventForm
          showEventForm={showEventForm}
          editingEvent={editingEvent}
          eventForm={eventForm}
          setEventForm={setEventForm}
          addEvent={addEvent}
          cancelEventForm={cancelEventForm}
          uploadingImage={uploadingImage}
          handleFileChange={handleFileChange}
          clearImagePreview={clearImagePreview}
          events={events}
          editEvent={editEvent}
          deleteEvent={deleteEvent}
          setShowEventForm={setShowEventForm}
          setEditingEvent={setEditingEvent}
          clubs={clubs}
          judges={judges}
        />
      </div>
    );
  }

  return (
    <div className="admin-view">
      <div className="card">
        <div className="card-header">
          <h2>📅 Gestión de Eventos</h2>
          <button
            onClick={() => {
              setEditingEvent(null);
              setEventForm({ 
                name: '', 
                date: '', 
                location: '', 
                discipline: '',
                category: '',
                apparatus: [],
                selectedClubs: [],
                selectedJudges: [],
                is_active: true,
                imageFile: null, 
                imagePreview: null 
              });
              setShowEventForm(true);
            }}
            className="btn btn-primary"
          >
             Nuevo Evento
          </button>
        </div>

        {/* Pestañas de Admin */}
        <div style={{ 
          padding: '16px 20px',
          borderBottom: '2px solid #e5e7eb',
          display: 'flex',
          gap: '16px',
          background: 'white'
        }}>
          <button
            onClick={() => setAdminTab('events')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'events' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'events' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease'
            }}
          >
            📅 Eventos
          </button>
          <button
            onClick={() => setAdminTab('judges')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'judges' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'judges' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease'
            }}
          >
            👨‍⚖️ Gestión de Jueces
          </button>
          <button
            onClick={() => setAdminTab('solicitudes')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'solicitudes' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'solicitudes' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            🔔 Solicitudes
            {accessRequests.length > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: -4,
                background: '#ef4444', color: 'white',
                borderRadius: '50%', width: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700
              }}>{accessRequests.length}</span>
            )}
          </button>
          <button
            onClick={() => setAdminTab('organizers')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'organizers' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'organizers' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            👥 Organizadores Pendientes
            {pendingOrganizers.length > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: -4,
                background: '#f59e0b', color: 'white',
                borderRadius: '50%', width: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700
              }}>{pendingOrganizers.length}</span>
            )}
          </button>
          <button
            onClick={() => setAdminTab('event-approvals')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'event-approvals' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'event-approvals' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            🛡️ Eventos Pendientes
            {pendingEvents.length > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: -4,
                background: '#f59e0b', color: 'white',
                borderRadius: '50%', width: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700
              }}>{pendingEvents.length}</span>
            )}
          </button>
          <button
            onClick={() => setAdminTab('clubs')}
            style={{
              padding: '8px 16px',
              background: adminTab === 'clubs' ? '#8b5cf6' : 'transparent',
              color: adminTab === 'clubs' ? 'white' : '#6b7280',
              border: 'none',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              fontSize: '0.95rem',
              transition: 'all 0.2s ease',
              position: 'relative'
            }}
          >
            🏢 Clubes
            {pendingClubsCount > 0 && (
              <span style={{
                position: 'absolute', top: 2, right: -4,
                background: '#ef4444', color: 'white',
                borderRadius: '50%', width: 18, height: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.7rem', fontWeight: 700
              }}>{pendingClubsCount}</span>
            )}
          </button>
        </div>

        {/* Filtros: Mes y Año */}
        {adminTab === 'events' && (
        <div style={{ 
          padding: '20px', 
          background: '#f8f9fa',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>Mes:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#111827',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'border-color 0.2s ease',
                minWidth: '150px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              {monthNames.map(month => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>Año:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#111827',
                fontWeight: '500',
                cursor: 'pointer',
                fontSize: '0.95rem',
                transition: 'border-color 0.2s ease',
                minWidth: '100px'
              }}
              onFocus={(e) => e.target.style.borderColor = '#8b5cf6'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            >
              {yearOptions.map(year => (
                <option key={year} value={String(year)}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '0.9rem', fontWeight: '500' }}>
            {filteredEvents.length} evento{filteredEvents.length !== 1 ? 's' : ''} encontrado{filteredEvents.length !== 1 ? 's' : ''}
          </div>
        </div>
        )}

        {/* Lista de Eventos Filtrados */}
        {adminTab === 'events' && (
          <>
            {filteredEvents.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>
                <p style={{ marginBottom: '16px' }}>No hay eventos en {monthNames.find(m => m.value === selectedMonth)?.label} de {selectedYear}</p>
              </div>
            ) : (
              <div style={{ padding: '0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151', width: '80px' }}>Logo</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Evento</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Fecha</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Ubicación</th>
                      <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Estado</th>
                      <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600', fontSize: '0.875rem', color: '#374151' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map((event, idx) => (
                      <tr key={event.id} style={{ 
                        borderBottom: '1px solid #e5e7eb',
                        background: idx % 2 === 0 ? 'white' : '#f9fafb',
                        opacity: event.is_active ? 1 : 0.6
                      }}>
                        <td style={{ padding: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                          {event.image_url ? (
                            <img
                              src={event.image_url}
                              alt={event.name}
                              style={{
                                width: '48px',
                                height: '48px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                                border: '1px solid #e5e7eb'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '6px',
                              background: '#f3f4f6',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid #e5e7eb',
                              fontSize: '24px'
                            }}>
                              🏆
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.95rem', fontWeight: '500', color: '#111827' }}>
                          {event.name}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.875rem', color: '#6b7280' }}>
                          {event.date}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '0.875rem', color: '#6b7280' }}>
                          {event.location || '—'}
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <ToggleSwitch
                            isActive={event.is_active}
                            onChange={() => handleToggleEventStatus(event)}
                            disabled={false}
                          />
                        </td>
                        <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <button
                              onClick={() => handleManageEvent(event)}
                              className="btn-icon"
                              title="Administrar evento"
                              style={{ color: '#10b981' }}
                            >
                              <Settings size={18} />
                            </button>
                            <button
                              onClick={() => handleShowQR(event)}
                              className="btn-icon"
                              title="Código QR"
                              style={{ color: '#8b5cf6' }}
                              disabled={codeLoading === event.id}
                            >
                              <QrCode size={18} />
                            </button>
                            <button
                              onClick={() => editEvent(event)}
                              className="btn-icon"
                              title="Editar evento"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => deleteEvent(event.id)}
                              className="btn-icon danger"
                              title="Eliminar evento"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* PESTAÑA: GESTIÓN DE JUECES */}
        {adminTab === 'judges' && (
          <div style={{ padding: '20px' }}>
            <JudgeInvitationManager 
              events={events || []}
              judges={judges || []}
              auth={auth}
            />
          </div>
        )}

        {/* PESTAÑA: SOLICITUDES DE ACCESO */}
        {adminTab === 'solicitudes' && (
          <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#18181b' }}>
                  <Bell size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                  Solicitudes de Acceso de Clubes
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#71717a' }}>
                  Filtra por evento para administrar solicitudes específicas.
                </p>
              </div>
              <button onClick={loadAccessRequests} style={{
                padding: '8px 16px', border: '1px solid #d4d4d8', borderRadius: 8,
                background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                fontSize: '0.85rem', color: '#52525b', fontWeight: 500
              }}>
                <RefreshCw size={14} /> Actualizar
              </button>
            </div>

            {/* Event Filter */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginRight: 8 }}>Filtrar por evento:</label>
              <select value={solicitudesEventFilter} onChange={(e) => setSolicitudesEventFilter(e.target.value)} style={{
                padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6,
                background: 'white', color: '#111827', fontWeight: 500, cursor: 'pointer',
                fontSize: '0.9rem', maxWidth: 400
              }}>
                <option value="all">Todos los eventos</option>
                {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name} — {ev.date}</option>)}
              </select>
            </div>

            {requestsLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando solicitudes...</div>
            ) : filteredSolicitudes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                <Bell size={40} strokeWidth={1} style={{ opacity: 0.4, marginBottom: 8 }} />
                <p>No hay solicitudes pendientes{solicitudesEventFilter !== 'all' ? ' para este evento' : ''}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {filteredSolicitudes.map(req => (
                  <div key={req.id} style={{
                    padding: 16, borderRadius: 12, border: '1px solid #fde68a',
                    background: '#fffbeb', display: 'flex', justifyContent: 'space-between',
                    alignItems: 'flex-start', gap: 16, flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                      <Building2 size={18} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
                      <div>
                        <h4 style={{ margin: 0, fontSize: '0.92rem', color: '#18181b' }}>{req.clubs?.name || 'Club'}</h4>
                        <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#71717a' }}>
                          {req.clubs?.email || ''} {req.clubs?.location ? `• ${req.clubs.location}` : ''}
                        </p>
                        {req.clubs?.phone && <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#52525b' }}>📞 {req.clubs.phone}</p>}
                        <p style={{ margin: '4px 0 0', fontSize: '0.78rem', color: '#8b5cf6' }}>
                          Evento: <strong>{req.events?.name || 'Evento'}</strong> — {req.events?.date || ''}
                        </p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => handleApproveRequest(req.id)} style={{
                        padding: '8px 14px', border: 'none', borderRadius: 8,
                        background: '#10b981', color: 'white', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: '0.82rem', fontWeight: 600
                      }}>
                        <CheckSquare size={14} /> Aprobar
                      </button>
                      <button onClick={() => handleRejectRequest(req.id)} style={{
                        padding: '8px 14px', border: 'none', borderRadius: 8,
                        background: '#ef4444', color: 'white', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4,
                        fontSize: '0.82rem', fontWeight: 600
                      }}>
                        <XSquare size={14} /> Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ========== Manage Event Modal ========== */}
      {managingEvent && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20
        }} onClick={() => setManagingEvent(null)}>
          <div style={{
            background: 'white', borderRadius: 16, padding: 32,
            maxWidth: 640, width: '100%', maxHeight: '80vh', overflowY: 'auto',
            border: '1px solid #e4e4e7'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem' }}><Settings size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Administrar Evento</h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#6b7280' }}>{managingEvent.name} — {managingEvent.date}</p>
              </div>
              <button onClick={() => setManagingEvent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6b7280' }}><X size={20} /></button>
            </div>

            {managingLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando...</div>
            ) : managingRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                <Building2 size={40} strokeWidth={1} style={{ opacity: 0.4, marginBottom: 8 }} />
                <p>No hay solicitudes para este evento</p>
              </div>
            ) : (
              <>
                {managingRequests.filter(r => r.status === 'pending').length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: '0.9rem', color: '#f59e0b', margin: '0 0 8px' }}>⏳ Pendientes ({managingRequests.filter(r => r.status === 'pending').length})</h4>
                    {managingRequests.filter(r => r.status === 'pending').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #fde68a', background: '#fffbeb', marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#f59e0b' }} />
                          <div>
                            <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>{req.clubs?.email || ''}</p>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => handleManageApprove(req.id)} style={{ padding: '6px 12px', border: 'none', borderRadius: 8, background: '#10b981', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', fontWeight: 600 }}>
                            <CheckSquare size={14} /> Aprobar
                          </button>
                          <button onClick={() => handleManageReject(req.id)} style={{ padding: '6px 12px', border: 'none', borderRadius: 8, background: '#ef4444', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', fontWeight: 600 }}>
                            <XSquare size={14} /> Rechazar
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {managingRequests.filter(r => r.status === 'approved').length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <h4 style={{ fontSize: '0.9rem', color: '#10b981', margin: '0 0 8px' }}>✅ Clubes Aprobados ({managingRequests.filter(r => r.status === 'approved').length})</h4>
                    {managingRequests.filter(r => r.status === 'approved').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #bbf7d0', background: '#f0fdf4', marginBottom: 8 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#10b981' }} />
                          <div>
                            <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                            <p style={{ margin: 0, fontSize: '0.78rem', color: '#6b7280' }}>{req.clubs?.email || ''}</p>
                          </div>
                        </div>
                        <button onClick={() => handleRemoveClubFromEvent(req.id)} style={{ padding: '6px 12px', border: 'none', borderRadius: 8, background: '#ef4444', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.8rem', fontWeight: 600 }}>
                          <UserMinus size={14} /> Eliminar
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {managingRequests.filter(r => r.status === 'rejected').length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '0.9rem', color: '#ef4444', margin: '0 0 8px' }}>❌ Rechazados ({managingRequests.filter(r => r.status === 'rejected').length})</h4>
                    {managingRequests.filter(r => r.status === 'rejected').map(req => (
                      <div key={req.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderRadius: 10, border: '1px solid #fecaca', background: '#fef2f2', marginBottom: 8, opacity: 0.7 }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <Building2 size={16} style={{ color: '#ef4444' }} />
                          <strong style={{ fontSize: '0.88rem' }}>{req.clubs?.name || 'Club'}</strong>
                        </div>
                        <span style={{ fontSize: '0.78rem', color: '#ef4444' }}>Rechazado</span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {adminTab === 'organizers' && (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#18181b' }}>
                <UserMinus size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Organizadores Pendientes de Aprobación
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#71717a' }}>
                Revisa los organizadores que se registraron y espera su aprobación para habilitar su acceso.
              </p>
            </div>
            <button onClick={loadPendingOrganizers} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#6b7280' }}>
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {pendingOrganizersLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando organizadores...</div>
          ) : pendingOrganizers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <UserMinus size={48} strokeWidth={1} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 4px' }}>Sin organizadores pendientes</p>
              <p style={{ fontSize: '0.85rem' }}>No hay nuevos registros de organizadores esperando aprobación en este momento.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {pendingOrganizers.map(org => (
                <div key={org.id} style={{ border: '1.5px solid #c7d2fe', borderRadius: 14, background: 'white', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', padding: '16px 20px', borderBottom: '1px solid #c7d2fe', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#18181b' }}>{org.name}</h4>
                      <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#4b5563' }}>{org.organization || 'Organización no especificada'}</p>
                    </div>
                    <span style={{ padding: '5px 12px', borderRadius: 8, background: '#e0e7ff', color: '#3730a3', fontSize: '0.78rem', fontWeight: 700, border: '1px solid #c7d2fe' }}>Pendiente</span>
                  </div>
                  <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
                    <div style={{ display: 'grid', gap: 10 }}>
                      <p style={{ margin: 0, color: '#374151', fontSize: '0.95rem' }}><strong>Correo:</strong> {org.email}</p>
                      <p style={{ margin: 0, color: '#374151', fontSize: '0.95rem' }}><strong>Teléfono:</strong> {org.phone || 'No proporcionado'}</p>
                      <p style={{ margin: 0, color: '#374151', fontSize: '0.95rem' }}><strong>Creado:</strong> {new Date(org.created_at).toLocaleDateString('es-ES')}</p>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 220 }}>
                      <textarea
                        placeholder="Notas para el administrador..."
                        value={organizerApprovalNotes[org.id] || ''}
                        onChange={(e) => setOrganizerApprovalNotes(prev => ({ ...prev, [org.id]: e.target.value }))}
                        style={{ width: '100%', minHeight: 90, borderRadius: 10, border: '1px solid #d1d5db', padding: 12, resize: 'vertical', fontSize: '0.9rem' }}
                      />
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button onClick={() => handleRejectOrganizer(org)} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #ef4444', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 600 }}>
                          Rechazar
                        </button>
                        <button onClick={() => handleApproveOrganizer(org)} style={{ padding: '10px 14px', borderRadius: 10, border: 'none', background: '#4f46e5', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
                          Aprobar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== PESTAÑA: EVENTOS PENDIENTES DE APROBACIÓN ========== */}
      {adminTab === 'event-approvals' && (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#18181b' }}>
                <ShieldCheck size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Eventos Pendientes de Aprobación
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#71717a' }}>
                Revisa y aprueba los eventos creados por organizadores. Una vez aprobados, serán visibles para clubes y público.
              </p>
            </div>
            <button onClick={loadPendingEvents} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#6b7280' }}>
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {pendingEventsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando...</div>
          ) : pendingEvents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <ShieldCheck size={48} strokeWidth={1} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 4px' }}>Sin eventos pendientes</p>
              <p style={{ fontSize: '0.85rem' }}>No hay eventos esperando aprobación en este momento.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {pendingEvents.map(event => {
                const org = event.organizers || {};
                return (
                  <div key={event.id} style={{
                    border: '1.5px solid #fde68a', borderRadius: 14, background: 'white',
                    overflow: 'hidden', transition: 'all 0.2s'
                  }}>
                    {/* Header */}
                    <div style={{
                      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                      padding: '16px 20px', borderBottom: '1px solid #fde68a',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {event.image_url && <img src={event.image_url} alt="" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', border: '2px solid white' }} />}
                        <div>
                          <h4 style={{ margin: 0, fontSize: '1.05rem', color: '#18181b' }}>{event.name}</h4>
                          <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#6b7280' }}>
                            📅 {event.date} {event.location ? `• 📍 ${event.location}` : ''} {event.discipline ? `• ${event.discipline}` : ''}
                          </p>
                        </div>
                      </div>
                      <span style={{
                        padding: '5px 12px', borderRadius: 8, background: '#fef3c7',
                        color: '#92400e', fontSize: '0.78rem', fontWeight: 700,
                        border: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        <Clock size={12} /> Pendiente
                      </span>
                    </div>

                    {/* Body */}
                    <div style={{ padding: '16px 20px' }}>
                      {/* Organizer info */}
                      <div style={{
                        display: 'flex', gap: 12, alignItems: 'flex-start',
                        padding: '12px 16px', background: '#f9fafb', borderRadius: 10,
                        border: '1px solid #f3f4f6', marginBottom: 12
                      }}>
                        <Building2 size={18} style={{ color: '#6b7280', flexShrink: 0, marginTop: 2 }} />
                        <div>
                          <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 700, color: '#18181b' }}>{org.name || 'Organizador'}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>{org.email || '—'}</p>
                          {org.organization && <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>🏢 {org.organization}</p>}
                          {org.phone && <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280' }}>📞 {org.phone}</p>}
                        </div>
                      </div>

                      {/* Services */}
                      {(event.live_support || event.flyer_service) && (
                        <div style={{ marginBottom: 12 }}>
                          <p style={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Servicios Adicionales Solicitados:</p>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {event.live_support && (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 8, background: '#ecfdf5',
                                color: '#065f46', fontSize: '0.8rem', fontWeight: 600,
                                border: '1px solid #a7f3d0'
                              }}>
                                <Headphones size={13} /> Asistencia en Vivo
                                <span style={{ background: '#d97706', color: 'white', padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700 }}>💰</span>
                              </span>
                            )}
                            {event.flyer_service && (
                              <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                padding: '6px 12px', borderRadius: 8, background: '#fdf2f8',
                                color: '#9d174d', fontSize: '0.8rem', fontWeight: 600,
                                border: '1px solid #fbcfe8'
                              }}>
                                <ImageIcon size={13} /> Flyer + Afiches QR
                                <span style={{ background: '#d97706', color: 'white', padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', fontWeight: 700 }}>💰</span>
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Notes input */}
                      <div style={{ marginBottom: 12 }}>
                        <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                          <MessageSquare size={12} /> Notas (requerido para rechazar)
                        </label>
                        <textarea
                          value={approvalNotes[event.id] || ''}
                          onChange={(e) => setApprovalNotes(prev => ({ ...prev, [event.id]: e.target.value }))}
                          placeholder="Notas sobre la aprobación o motivo del rechazo..."
                          style={{
                            width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb',
                            borderRadius: 10, fontSize: '0.85rem', resize: 'vertical',
                            minHeight: 60, fontFamily: 'inherit', boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleRejectEvent(event.id)}
                          style={{
                            padding: '10px 20px', border: 'none', borderRadius: 10,
                            background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: '0.88rem', fontWeight: 700,
                            border: '1px solid #fecaca', transition: 'all 0.2s'
                          }}
                        >
                          <XSquare size={16} /> Rechazar
                        </button>
                        <button
                          onClick={() => handleApproveEvent(event.id)}
                          style={{
                            padding: '10px 24px', border: 'none', borderRadius: 10,
                            background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                            fontSize: '0.88rem', fontWeight: 700,
                            boxShadow: '0 2px 8px rgba(16,185,129,0.3)', transition: 'all 0.2s'
                          }}
                        >
                          <CheckSquare size={16} /> Aprobar y Activar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== PESTAÑA: GESTIÓN DE CLUBES ========== */}
      {adminTab === 'clubs' && (
        <div style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#18181b' }}>
                <Building2 size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                Gestión de Clubes
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#71717a' }}>
                Aprobar/rechazar registros, suspender cuentas, habilitar Análisis IA y eliminar clubes.
              </p>
            </div>
            <button onClick={loadAllClubs} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8, background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', color: '#6b7280' }}>
              <RefreshCw size={14} /> Actualizar
            </button>
          </div>

          {/* Filtros */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'pending_approval', label: 'Pendientes', color: '#ef4444' },
              { key: 'approved', label: 'Aprobados', color: '#10b981' },
              { key: 'rejected', label: 'Rechazados', color: '#71717a' },
              { key: 'all', label: 'Todos', color: '#8b5cf6' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setClubsFilter(f.key)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  border: clubsFilter === f.key ? `2px solid ${f.color}` : '1px solid #e5e7eb',
                  background: clubsFilter === f.key ? `${f.color}15` : 'white',
                  color: clubsFilter === f.key ? f.color : '#6b7280',
                  fontWeight: clubsFilter === f.key ? 700 : 500,
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                {f.label}
                {f.key === 'pending_approval' && pendingClubsCount > 0 && ` (${pendingClubsCount})`}
              </button>
            ))}
            <div style={{ position: 'relative', flex: 1, minWidth: 220, maxWidth: 340, marginLeft: 'auto' }}>
              <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Buscar nombre, email, ubicación..."
                value={clubsSearch}
                onChange={e => setClubsSearch(e.target.value)}
                style={{ width: '100%', padding: '8px 12px 8px 34px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: '0.85rem' }}
              />
            </div>
          </div>

          {clubsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Cargando clubes...</div>
          ) : filteredClubs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
              <Building2 size={48} strokeWidth={1} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 4px' }}>
                {clubsSearch ? 'No se encontraron clubes' : `No hay clubes ${clubsFilter === 'all' ? '' : clubsFilter === 'pending_approval' ? 'pendientes' : clubsFilter === 'approved' ? 'aprobados' : 'rechazados'}`}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredClubs.map(club => {
                const status = club.approval_status || 'approved';
                const isPending = status === 'pending_approval';
                const isRejected = status === 'rejected';
                const isExpanded = expandedClubId === club.id;
                const statusBadge = isPending
                  ? { bg: '#fef3c7', border: '#fde68a', color: '#92400e', label: 'Pendiente' }
                  : isRejected
                  ? { bg: '#fee2e2', border: '#fecaca', color: '#b91c1c', label: 'Rechazado' }
                  : { bg: '#d1fae5', border: '#a7f3d0', color: '#065f46', label: 'Aprobado' };

                return (
                  <div key={club.id} style={{
                    border: `1.5px solid ${statusBadge.border}`, borderRadius: 12, background: 'white',
                    overflow: 'hidden', transition: 'all 0.2s',
                    opacity: club.is_active === false && !isPending ? 0.7 : 1,
                  }}>
                    {/* Header */}
                    <div style={{
                      padding: '14px 18px', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 12, flexWrap: 'wrap',
                      background: isPending ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' : 'white',
                      borderBottom: isExpanded ? `1px solid ${statusBadge.border}` : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => setExpandedClubId(isExpanded ? null : club.id)}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 260 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                          🏢
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <h4 style={{ margin: 0, fontSize: '1rem', color: '#18181b', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {club.name}
                            {club.is_active === false && !isPending && (
                              <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 6, background: '#fee2e2', color: '#b91c1c', fontSize: '0.7rem', fontWeight: 700 }}>
                                SUSPENDIDO
                              </span>
                            )}
                          </h4>
                          <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#6b7280', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            {club.email && <span><Mail size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{club.email}</span>}
                            {club.location && <span><MapPin size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />{club.location}</span>}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{
                          padding: '4px 10px', borderRadius: 8, background: statusBadge.bg,
                          color: statusBadge.color, fontSize: '0.75rem', fontWeight: 700,
                          border: `1px solid ${statusBadge.border}`,
                        }}>
                          {statusBadge.label}
                        </span>
                        {club.pose_analysis_enabled && (
                          <span title="Análisis IA activado" style={{
                            padding: '4px 10px', borderRadius: 8, background: '#ede9fe',
                            color: '#6d28d9', fontSize: '0.72rem', fontWeight: 700, border: '1px solid #c4b5fd',
                            display: 'flex', alignItems: 'center', gap: 4,
                          }}>
                            <Sparkles size={11} /> IA
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Expanded body */}
                    {isExpanded && (
                      <div style={{ padding: '16px 18px', display: 'grid', gap: 16 }}>
                        {/* Info grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, fontSize: '0.85rem', color: '#374151' }}>
                          <div><strong>Teléfono:</strong> {club.phone || '—'}</div>
                          <div><strong>Federado:</strong> {club.is_federated ? 'Sí' : 'No'}</div>
                          <div><strong>Creado:</strong> {club.created_at ? new Date(club.created_at).toLocaleDateString('es-ES') : '—'}</div>
                          {club.approved_at && <div><strong>{isRejected ? 'Rechazado' : 'Aprobado'}:</strong> {new Date(club.approved_at).toLocaleDateString('es-ES')}</div>}
                        </div>
                        {club.disciplines && Array.isArray(club.disciplines) && club.disciplines.length > 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                            <strong>Disciplinas:</strong> {club.disciplines.join(', ')}
                          </div>
                        )}
                        {club.admin_notes && (
                          <div style={{ padding: 10, background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.85rem', color: '#4b5563' }}>
                            <strong>Notas previas:</strong> {club.admin_notes}
                          </div>
                        )}

                        {/* Notes input (solo para pendientes) */}
                        {isPending && (
                          <textarea
                            placeholder="Notas del administrador (obligatorio para rechazar)..."
                            value={clubAdminNotes[club.id] || ''}
                            onChange={(e) => setClubAdminNotes(prev => ({ ...prev, [club.id]: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            style={{ width: '100%', minHeight: 70, borderRadius: 8, border: '1px solid #d1d5db', padding: 10, resize: 'vertical', fontSize: '0.85rem', fontFamily: 'inherit' }}
                          />
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'flex-end' }}>
                          {isPending && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleRejectClub(club); }}
                                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444', background: '#fee2e2', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}
                              >
                                <XSquare size={14} /> Rechazar
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleApproveClub(club); }}
                                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#10b981', color: 'white', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}
                              >
                                <CheckSquare size={14} /> Aprobar
                              </button>
                            </>
                          )}
                          {!isPending && (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleTogglePoseAnalysis(club); }}
                                title={club.pose_analysis_enabled ? 'Desactivar Análisis IA' : 'Activar Análisis IA'}
                                style={{
                                  padding: '8px 14px', borderRadius: 8,
                                  border: `1px solid ${club.pose_analysis_enabled ? '#8b5cf6' : '#d1d5db'}`,
                                  background: club.pose_analysis_enabled ? '#ede9fe' : 'white',
                                  color: club.pose_analysis_enabled ? '#6d28d9' : '#6b7280',
                                  cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                                  display: 'flex', alignItems: 'center', gap: 6,
                                }}
                              >
                                <Sparkles size={14} /> {club.pose_analysis_enabled ? 'IA Activada' : 'Activar IA'}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleClubActive(club); }}
                                title={club.is_active ? 'Suspender acceso' : 'Reactivar acceso'}
                                style={{
                                  padding: '8px 14px', borderRadius: 8,
                                  border: `1px solid ${club.is_active ? '#f59e0b' : '#10b981'}`,
                                  background: club.is_active ? '#fffbeb' : '#d1fae5',
                                  color: club.is_active ? '#b45309' : '#065f46',
                                  cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
                                  display: 'flex', alignItems: 'center', gap: 6,
                                }}
                              >
                                <Power size={14} /> {club.is_active ? 'Suspender' : 'Reactivar'}
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteClub(club); }}
                                title="Eliminar permanentemente"
                                style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ef4444', background: 'white', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}
                              >
                                <Trash2 size={14} /> Eliminar
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ========== QR Modal ========== */}
      {qrModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: 20
        }} onClick={() => setQrModal(null)}>
          <div style={{
            background: 'white', borderRadius: 16, padding: 32,
            maxWidth: 400, width: '100%', textAlign: 'center',
            border: '1px solid #e4e4e7'
          }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontSize: '1.15rem', color: '#18181b' }}>Código de Acceso</h3>
            <p style={{ margin: '0 0 24px', color: '#8b5cf6', fontSize: '0.88rem', fontWeight: 500 }}>{qrModal.eventName}</p>
            <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 20px' }}>
              <QRCodeSVG value={qrModal.code} size={200} bgColor="#ffffff" fgColor="#1a1a1a" level="H" includeMargin />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 16px', background: '#f4f4f5', borderRadius: 10, margin: '0 0 12px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '1.2rem', fontWeight: 700, color: '#18181b', letterSpacing: 2 }}>{qrModal.code}</span>
              <button onClick={() => handleCopyCode(qrModal.code)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#71717a', padding: 4 }}>
                {copiedCode === qrModal.code ? <CheckCircle size={18} style={{ color: '#10b981' }} /> : <Copy size={18} />}
              </button>
            </div>
            {copiedCode === qrModal.code && <p style={{ fontSize: '0.8rem', color: '#10b981', margin: '0 0 8px' }}>¡Código copiado!</p>}
            <p style={{ color: '#71717a', fontSize: '0.8rem', margin: '8px 0 20px', lineHeight: 1.4 }}>Comparte este código o QR con los clubes para que soliciten participar.</p>
            <button onClick={() => setQrModal(null)} style={{
              width: '100%', padding: '10px', border: '1px solid #e4e4e7', borderRadius: 8,
              background: 'white', cursor: 'pointer', fontSize: '0.9rem', color: '#52525b'
            }}>Cerrar</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminView;